import React from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Point } from '@visx/point';
import { Line, LineRadial } from '@visx/shape';
import { Chip } from '@mui/material';

const graphFill = '#3BC75E';
const graphStroke = '#2EB450';
const lineColor = '#9BB3C5';
const background = 'transparent';
const degrees = 360;

const chipText = { 0: 'red', 1: '#95BC4C', 2: '#FFF', 3: '#FFF' };
const chipBg = { 0: '#FFF', 1: '#D0F38B', 2: '#F99407', 3: '#3BC75E' };

const y = (d) => Number(d.frequency ?? d.value ?? 0);

/** 중대재해 처벌법 레이더 — 데이터 없을 때 축·격자만 표시 (가점 사항 제외 8항목) */
export const SERIOUS_RADAR_EMPTY_DATA = [
  { subject: '경영자 안전보건경영활동(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '위험성 평가(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '안전보건교육(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '근로자 참여(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '중대재해 대응조치(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '도급.용역.위탁 안전보건확보(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '평가 및 개선(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '안전문화 및 안전의식(0)', value: 0, fullMark: 100, level: 0 },
];

const genAngles = (length) =>
  [...new Array(length + 1)].map((_, i) => ({
    angle:
      i * (degrees / length) + (length % 2 === 0 ? 0 : degrees / length / 2),
  }));

const genPoints = (length, radius) => {
  const step = (Math.PI * 2) / length;
  const startAngle = -Math.PI / 2;
  return [...new Array(length)].map((_, i) => {
    const angle = i * step + startAngle;
    return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
  });
};

function genPolygonPoints(dataArray, scale, getValue) {
  const step = (Math.PI * 2) / dataArray.length;
  const points = new Array(dataArray.length).fill({ x: 0, y: 0 });
  const pointString = dataArray.reduce((res, d, i) => {
    const value = getValue(d);
    const angle = i * step - Math.PI / 2;
    const xVal = scale(value) * Math.cos(angle);
    const yVal = scale(value) * Math.sin(angle);
    points[i] = { x: xVal, y: yVal };
    return res + `${xVal},${yVal} `;
  }, '');
  return { points, pointString };
}

// 크기 줄이고 margin도 줄임
const defaultMargin = { top: 50, left: 50, right: 50, bottom: 50 };

export default function RadarChart({
  data = [],
  levels = 4,
  margin = defaultMargin,
  /** true면 데이터 없을 때 0점 8축 골격 표시 (중대재해 대시보드) */
  showEmptySkeleton = true,
}) {
  const isEmptyPlaceholder = !data?.length;
  const displayData =
    !isEmptyPlaceholder
      ? data
      : showEmptySkeleton
        ? SERIOUS_RADAR_EMPTY_DATA
        : [];

  const baseWidth = 400; // 600 → 400
  const baseHeight = 400; // 600 → 400

  const xMax = baseWidth - margin.left - margin.right;
  const yMax = baseHeight - margin.top - margin.bottom;
  const radius = Math.min(xMax, yMax) / 2;

  if (displayData.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${baseWidth} ${baseHeight}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }

  const radialScale = scaleLinear({
    range: [0, Math.PI * 2],
    domain: [degrees, 0],
  });

  const yScale = scaleLinear({
    range: [0, radius],
    domain: [0, 100],
  });

  const webs = genAngles(displayData.length);
  const points = genPoints(displayData.length, radius);
  const polygonPoints = genPolygonPoints(
    displayData,
    (d) => yScale(d) ?? 0,
    y
  );
  const zeroPoint = new Point({ x: 0, y: 0 });
  const labelPoints = genPoints(displayData.length, radius + 30);

  return (
    // div 제거, svg가 부모 크기 100% 따라감
    <svg
      viewBox={`0 0 ${baseWidth} ${baseHeight}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect fill={background} width={baseWidth} height={baseHeight} rx={14} />
      <Group top={baseHeight / 2} left={baseWidth / 2}>
        {[...new Array(levels)].map((_, i) => (
          <LineRadial
            key={`web-${i}`}
            data={webs}
            angle={(d) => radialScale(d.angle) ?? 0}
            radius={((i + 1) * radius) / levels}
            fill="none"
            stroke={lineColor}
            strokeWidth={1}
            strokeOpacity={0.8}
          />
        ))}
        {[...new Array(displayData.length)].map((_, i) => (
          <Line
            key={`radar-line-${i}`}
            from={zeroPoint}
            to={points[i]}
            stroke={lineColor}
          />
        ))}
        <polygon
          points={polygonPoints.pointString}
          fill={graphFill}
          fillOpacity={isEmptyPlaceholder ? 0.2 : 0.47}
          stroke={graphStroke}
          strokeWidth={1}
        />
        {labelPoints.map((point, i) => {
          const strSplit = displayData[i].subject.match(/(.*)\((\d+)\)/);
          if (!strSplit) return null;

          const content = strSplit[1].trim();
          const score = strSplit[2];

          let xCorrection = 0;
          if (content.includes('경영자')) xCorrection = 110;
          else if (content.includes('위험성')) xCorrection = -20;
          else if (content.includes('중대재해')) xCorrection = 80;
          else if (content.includes('도급')) xCorrection = 250;
          else if (content.includes('평가')) xCorrection = 150;
          else if (content.includes('안전문화')) xCorrection = 205;

          return (
            <foreignObject
              key={`tick-${i}`}
              x={point.x - xCorrection}
              y={point.y - 15}
              width={280}
              height={40}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  style={{ fontWeight: 500, color: '#555', fontSize: '18px' }}
                >
                  {content}
                </span>
                <Chip
                  label={score}
                  size="medium"
                  sx={{
                    ml: '8px',
                    bgcolor: chipBg[displayData[i].level],
                    color: chipText[displayData[i].level],
                    fontSize: '18px',
                    fontWeight: 'bold',
                  }}
                />
              </div>
            </foreignObject>
          );
        })}
      </Group>
    </svg>
  );
}
